/* ─── Layout ─── */
.page {
  height: 100vh;
  height: 100dvh;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.bgGradient {
  position: fixed;
  inset: 0;
  background:
    radial-gradient(ellipse 80% 50% at 50% -20%, rgba(34, 211, 238, 0.06), transparent),
    radial-gradient(ellipse 60% 40% at 100% 100%, rgba(251, 146, 60, 0.04), transparent),
    radial-gradient(ellipse 40% 30% at 0% 80%, rgba(34, 211, 238, 0.04), transparent);
  pointer-events: none;
  z-index: 0;
}

/* Subtle grid texture matching docs */
.bgGradient::after {
  content: "";
  position: fixed;
  inset: 0;
  pointer-events: none;
  background:
    repeating-linear-gradient(
      0deg, transparent, transparent 49px,
      rgba(148, 163, 184, 0.02) 49px, rgba(148, 163, 184, 0.02) 50px
    ),
    repeating-linear-gradient(
      90deg, transparent, transparent 49px,
      rgba(148, 163, 184, 0.02) 49px, rgba(148, 163, 184, 0.02) 50px
    );
}

/* ─── Top bar ─── */
.topBar {
  position: sticky;
  top: 0;
  z-index: 10;
  background: transparent;
  backdrop-filter: blur(20px) saturate(1.2);
  border-bottom: 1px solid var(--card-border);
}

.topBarInner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 2rem;
  height: 52px;
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.topBarBrand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  flex-shrink: 0;
}

.topBarLogo {
  font-family: var(--font-heading);
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-accent);
  letter-spacing: -0.02em;
}

.topBarDivider {
  width: 1px;
  height: 20px;
  background: var(--card-border);
  flex-shrink: 0;
}

.topBarTitle {
  font-family: var(--font-heading);
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  flex-shrink: 0;
}

.topBarSearch {
  flex: 1;
  max-width: 400px;
  margin-left: auto;
  position: relative;
}

.searchIcon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  opacity: 0.5;
  pointer-events: none;
}

.searchInput {
  width: 100%;
  padding: 0.45rem 0.75rem 0.45rem 2.1rem;
  border-radius: 7px;
  border: 1px solid var(--border-color, rgba(148, 163, 184, 0.18));
  background: var(--bg-card);
  color: var(--text-primary);
  font-size: 0.82rem;
  outline: none;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
}

.searchInput::placeholder {
  color: var(--text-secondary);
  opacity: 0.4;
}

.searchInput:focus {
  border-color: var(--accent-color, rgba(34, 211, 238, 0.3));
  background: var(--bg-card);
  box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.06);
}

.publishCta {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.9rem;
  border-radius: 7px;
  font-size: 0.78rem;
  font-weight: 600;
  text-decoration: none;
  background: var(--text-accent);
  color: var(--bg-primary);
  border: none;
  cursor: pointer;
  transition: filter 0.2s, transform 0.15s;
  flex-shrink: 0;
  white-space: nowrap;
  letter-spacing: -0.01em;
}

.publishCta:hover {
  filter: brightness(1.15);
  transform: translateY(-1px);
}

/* ─── Layout ─── */
.layout {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  max-width: 100%;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 1.5rem;
}

.layoutWithDetail {
  grid-template-columns: 200px 1fr 420px;
}

/* ─── Sidebar ─── */
.sidebar {
  overflow-y: auto;
  border-right: 1px solid var(--card-border);
  padding: 1.25rem 1rem 1.25rem 0;
}

.sidebarTitle {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
  margin: 0 0 0.75rem;
  padding: 0 0.5rem;
}

.sidebarNav {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.sidebarItem {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  border-left: 3px solid transparent;
  text-decoration: none;
  font-size: 0.88rem;
  color: var(--text-secondary);
  transition: all 0.15s ease;
  border-top: none;
  border-bottom: none;
  border-right: none;
  background: none;
  cursor: pointer;
  width: 100%;
  text-align: left;
}

.sidebarItem:hover {
  background: rgba(34, 211, 238, 0.04);
  color: var(--text-primary);
}

.sidebarItemActive {
  background: linear-gradient(135deg, rgba(34, 211, 238, 0.06), rgba(251, 146, 60, 0.04));
  border-left-color: var(--accent-warm);
  color: var(--text-primary);
  font-weight: 600;
}

.sidebarItemIcon {
  display: none;
}

.sidebarItemLabel {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebarItemCount {
  font-size: 0.7rem;
  color: var(--text-secondary);
  opacity: 0.35;
  font-variant-numeric: tabular-nums;
  margin-left: auto;
}

/* ─── Feed ─── */
.feed {
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow-y: auto;
  padding: 1.25rem 0;
}

.feedSearchBar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  margin-bottom: 0.75rem;
}

.feedSearchIcon {
  color: var(--text-secondary);
  flex-shrink: 0;
}

.feedSearchInput {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--text-primary);
  font-size: 0.85rem;
  font-family: var(--font-body);
}

.feedSearchInput::placeholder {
  color: var(--text-secondary);
}

.feedHeader {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.feedTabs {
  display: flex;
  gap: 0.25rem;
}

.feedTab {
  padding: 0.35rem 0.75rem;
  font-size: 0.8rem;
  font-weight: 500;
  background: transparent;
  color: var(--text-secondary);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  opacity: 0.6;
}

.feedTab:hover {
  color: var(--text-primary);
  opacity: 0.8;
}

.feedTabActive {
  color: var(--text-primary);
  background: rgba(148, 163, 184, 0.08);
  opacity: 1;
  font-weight: 600;
}

.feedCount {
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-left: auto;
}

.viewToggle {
  display: flex;
  gap: 2px;
  margin-left: 0.5rem;
}

.viewBtn {
  padding: 0.4rem;
  border-radius: 6px;
  border: none;
  background: none;
  color: var(--text-secondary);
  cursor: pointer;
  opacity: 0.4;
  transition: opacity 0.15s, color 0.15s;
  display: flex;
  align-items: center;
}

.viewBtn:hover {
  opacity: 0.7;
}

.viewBtnActive {
  opacity: 1;
  color: var(--text-accent);
}

.loadMoreBtn {
  display: block;
  margin: 1.5rem auto 0;
  padding: 0.6rem 1.5rem;
  background: transparent;
  border: 1px solid var(--card-border, rgba(255, 255, 255, 0.08));
  border-radius: 8px;
  color: var(--text-secondary, #94a3b8);
  font-size: 0.82rem;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}

.loadMoreBtn:hover {
  border-color: var(--text-accent, #22d3ee);
  color: var(--text-primary, #e2e8f0);
}

/* ─── Grid view for watch cards ─── */
.watchGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.gridCard {
  display: flex;
  flex-direction: column;
  text-decoration: none;
  padding: 1.1rem 1.25rem;
  border-radius: 12px;
  border: 1px solid var(--card-border);
  background: var(--card-bg);
  backdrop-filter: blur(12px);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.gridCard::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--text-accent), var(--accent-warm));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.gridCard:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(34, 211, 238, 0.04);
  border-color: var(--text-accent);
}

.gridCard:hover::before {
  opacity: 1;
}

.gridCard:hover .gridCardTitle {
  color: var(--text-accent);
}

.gridCardSelected {
  border-color: var(--text-accent);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(34, 211, 238, 0.18);
  transform: translateY(-2px);
}

.gridCardSelected::before {
  opacity: 1;
}

.gridCardSelected .gridCardTitle {
  color: var(--text-accent);
}

.gridCardTop {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.65rem;
}

.gridCardFavicon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--bg-secondary);
  border: 1px solid var(--card-border);
  flex-shrink: 0;
  object-fit: cover;
}

.gridCardDomain {
  font-size: 0.75rem;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.gridCardTitle {
  font-family: var(--font-heading);
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.15s;
}

.gridCardMeta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 0.6rem;
  border-top: 1px solid var(--card-border);
}

.gridCardBadge {
  font-size: 0.68rem;
  color: var(--text-accent);
  background: rgba(34, 211, 238, 0.08);
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  font-weight: 500;
}

.gridCardClones {
  font-size: 0.72rem;
  color: var(--text-secondary);
  margin-left: auto;
  opacity: 0.6;
}

.gridCardAuthor {
  font-size: 0.72rem;
  color: var(--text-secondary);
  opacity: 0.5;
}

/* ─── Watch card (feed item) ─── */
.card {
  display: block;
  text-decoration: none;
  padding: 1rem 1.1rem;
  margin-bottom: 0.25rem;
  border-radius: 10px;
  border: 1px solid var(--card-border);
  background: var(--card-bg);
  transition: all 0.2s ease;
  position: relative;
  cursor: pointer;
}

.card:hover {
  background: rgba(30, 41, 59, 0.85);
  border-color: rgba(148, 163, 184, 0.2);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.cardSelected {
  background: rgba(34, 211, 238, 0.06);
  border-color: var(--text-accent);
  border-left: 3px solid var(--text-accent);
  padding-left: calc(1.1rem - 2px);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(34, 211, 238, 0.18);
}

.card:hover .cardTitle,
.cardSelected .cardTitle {
  color: var(--text-accent);
}

.cardTop {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.4rem;
}

.cardAuthor {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.cardAuthorName {
  color: var(--text-primary);
  font-weight: 500;
}

.cardDot {
  font-size: 0.4rem;
  color: var(--text-secondary);
  opacity: 0.3;
}

.cardTime {
  font-size: 0.72rem;
  color: var(--text-secondary);
  opacity: 0.5;
}

.cardBody {
  display: flex;
  gap: 1.25rem;
  align-items: flex-start;
}

.cardContent {
  flex: 1;
  min-width: 0;
}

.cardTitle {
  font-family: var(--font-heading);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.25rem;
  line-height: 1.4;
  letter-spacing: -0.01em;
  transition: color 0.15s;
}

.cardTitleLink {
  color: inherit;
  text-decoration: none;
}

.cardTitleLink:hover {
  text-decoration: underline;
}

.cardUrl {
  font-size: 0.78rem;
  color: var(--text-secondary);
  margin: 0 0 0.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.6;
}

.cardMeta {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.cardBadge {
  font-size: 0.7rem;
  color: var(--text-accent);
  background: rgba(34, 211, 238, 0.06);
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.cardFilters {
  font-size: 0.7rem;
  color: var(--text-secondary);
  opacity: 0.5;
}

.cardActions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.6rem;
}

.cardStat {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  opacity: 0.6;
}

.cardStatIcon {
  width: 14px;
  height: 14px;
}

.cardAddBtn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-accent);
  margin-left: auto;
  padding: 0.25rem 0.6rem;
  border-radius: 5px;
  border: 1px solid rgba(34, 211, 238, 0.15);
  background: transparent;
  cursor: pointer;
  transition: all 0.15s, opacity 0.2s;
  text-decoration: none;
  letter-spacing: 0.01em;
  opacity: 1;
  pointer-events: auto;
}

/* Revealed on hover only where there is a pointer to hover with. Unconditionally
   hidden, this made the list's primary action invisible on a phone unless a card
   happened to be selected - so the main thing the page is for could not be done
   on the device most people browse it from. */
@media (hover: hover) {
  .cardAddBtn {
    opacity: 0;
    pointer-events: none;
  }

  .card:hover .cardAddBtn,
  .cardSelected .cardAddBtn,
  .cardAddBtn:focus-visible {
    opacity: 1;
    pointer-events: auto;
  }
}

.cardAddBtn:hover {
  background: rgba(34, 211, 238, 0.08);
  border-color: rgba(34, 211, 238, 0.35);
}

.cardFavicon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: var(--bg-secondary);
  border: 1px solid rgba(148, 163, 184, 0.08);
  flex-shrink: 0;
  object-fit: cover;
}

/* ─── Empty state ─── */
.empty {
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
}

.emptyIcon {
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
  opacity: 0.5;
}

.emptyHint {
  font-size: 0.85rem;
  opacity: 0.6;
  margin-top: 0.5rem;
}

/* ─── Detail panel (right side, LinkedIn-style) ─── */
.detailPanel {
  overflow-y: auto;
  border-left: 1px solid var(--card-border);
  padding: 1.25rem 0 1.25rem 1.5rem;
  position: relative;
}

.detailPanel::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(
    180deg,
    var(--text-accent) 0%,
    rgba(34, 211, 238, 0.3) 40%,
    transparent 100%
  );
  z-index: 1;
}

.detailLoading {
  padding: 3rem 1rem;
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.85rem;
}

.detailContent {
  position: relative;
}

.detailClose {
  position: absolute;
  top: 0;
  right: 0;
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 6px;
  transition: color 0.15s, background 0.15s;
  opacity: 0.5;
}

.detailClose:hover {
  color: var(--text-primary);
  background: rgba(148, 163, 184, 0.08);
  opacity: 1;
}

.detailHead {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  margin-bottom: 1rem;
  padding-right: 2rem;
}

.detailFavicon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--bg-secondary);
  border: 1px solid rgba(148, 163, 184, 0.08);
  flex-shrink: 0;
}

.detailTitle {
  font-family: var(--font-heading);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.15rem;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.detailUrl {
  color: var(--text-secondary);
  font-size: 0.8rem;
  text-decoration: none;
  word-break: break-all;
  opacity: 0.7;
}

.detailUrl:hover {
  color: var(--text-accent);
  opacity: 1;
}

.detailMeta {
  display: flex;
  gap: 0.75rem;
  font-size: 0.78rem;
  color: var(--text-secondary);
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--card-border);
  flex-wrap: wrap;
  align-items: center;
}

.detailBadge {
  font-size: 0.72rem;
  color: var(--text-accent);
  background: rgba(34, 211, 238, 0.06);
  padding: 0.2rem 0.55rem;
  border-radius: 4px;
  font-weight: 500;
}

.detailConfig {
  margin-bottom: 1.25rem;
}

.detailConfigTitle {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 0.75rem;
  opacity: 0.7;
}

.detailConfigGrid {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.detailConfigRow {
  display: flex;
  gap: 0.75rem;
  font-size: 0.82rem;
  padding: 0.35rem 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.04);
}

.detailConfigRow:last-child {
  border-bottom: none;
}

.detailConfigLabel {
  color: var(--text-secondary);
  min-width: 90px;
  flex-shrink: 0;
  font-size: 0.78rem;
  opacity: 0.7;
}

.detailConfigValue {
  color: var(--text-primary);
  word-break: break-all;
  font-size: 0.82rem;
}

.detailCta {
  margin-bottom: 1rem;
}

.detailCtaBtn {
  display: block;
  width: 100%;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  text-align: center;
  background: var(--text-accent);
  color: var(--bg-primary);
  border: none;
  cursor: pointer;
  transition: filter 0.2s, transform 0.15s;
}

.detailCtaBtn:hover {
  filter: brightness(1.15);
  transform: translateY(-1px);
}

.detailCtaBtn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.detailCtaSuccess {
  text-align: center;
  color: #22c55e;
  font-weight: 600;
  font-size: 0.9rem;
  margin: 0 0 0.5rem;
}

/* ─── Social share row ─── */
.detailShareRow {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--card-border);
}

.detailShareBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--card-border);
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.15s;
}

.detailShareBtn:hover {
  color: var(--text-primary);
  border-color: var(--text-accent);
  background: rgba(34, 211, 238, 0.04);
}

.detailPermalink {
  display: block;
  text-align: center;
  margin-top: 0.75rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  text-decoration: none;
  opacity: 0.5;
  transition: opacity 0.15s, color 0.15s;
}

.detailPermalink:hover {
  opacity: 1;
  color: var(--text-accent);
}

/* ─── Watch detail page (standalone $slug route, share-page style) ─── */

.wdMain {
  min-height: 100vh;
  min-height: 100dvh;
  background: var(--bg-primary);
  color: var(--text-primary);
  position: relative;
}

.wdContainer {
  max-width: 580px;
  margin: 0 auto;
  padding: 1.5rem 1.5rem 3rem;
}

.wdCard {
  width: 100%;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 14px;
  padding: 1.75rem;
  position: relative;
  box-shadow: var(--card-shadow);
}

.wdHeader {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  margin-bottom: 0.5rem;
}

.wdFaviconWrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.wdFavicon {
  width: 22px;
  height: 22px;
  border-radius: 3px;
}

.wdFaviconFallback {
  width: 18px;
  height: 18px;
  color: var(--text-secondary);
}

.wdHeaderText {
  min-width: 0;
}

.wdTitle {
  font-family: var(--font-heading);
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-primary);
  letter-spacing: -0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wdUrl {
  display: block;
  font-family: var(--font-body);
  font-size: 0.8125rem;
  color: var(--text-accent);
  text-decoration: none;
  margin-top: 0.125rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.15s;
}

.wdUrl:hover {
  color: var(--text-primary);
}

.wdMonitorType {
  font-family: var(--font-body);
  font-size: 0.72rem;
  color: var(--text-secondary);
  padding: 0.2rem 0.55rem;
  background: rgba(148, 163, 184, 0.08);
  border-radius: 4px;
  font-weight: 500;
}

.wdMeta {
  display: flex;
  gap: 0.5rem;
  font-size: 0.78rem;
  color: var(--text-secondary);
  margin-top: 1rem;
  flex-wrap: wrap;
  align-items: center;
}

.wdMetaItem {
  font-size: 0.72rem;
  color: var(--text-secondary);
}

.wdMetaBadge {
  font-size: 0.72rem;
  color: var(--text-accent);
  background: rgba(34, 211, 238, 0.06);
  padding: 0.2rem 0.55rem;
  border-radius: 4px;
  font-weight: 500;
  text-decoration: none;
  transition: background 0.15s;
}

.wdMetaBadge:hover {
  background: rgba(34, 211, 238, 0.12);
}

.wdDivider {
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    var(--border-color) 20%,
    var(--border-color) 80%,
    transparent
  );
  margin: 1.25rem 0;
}

.wdSectionTitle {
  font-family: var(--font-body);
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
  margin: 0 0 0.75rem;
}

.wdConfigGrid {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.5rem 1rem;
  align-items: baseline;
}

.wdConfigLabel {
  font-family: var(--font-body);
  font-size: 0.8125rem;
  color: var(--text-secondary);
  white-space: nowrap;
}

.wdConfigValue {
  font-family: 'SF Mono', 'Fira Code', 'JetBrains Mono', monospace;
  font-size: 0.8125rem;
  color: var(--text-primary);
  word-break: break-word;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  background: rgba(148, 163, 184, 0.06);
}

.wdError {
  text-align: center;
  color: var(--color-error);
  font-family: var(--font-body);
  font-size: 0.8125rem;
  padding: 0.75rem;
  margin-top: 0.75rem;
  background: var(--color-error-light);
  border: 1px solid var(--color-error-border);
  border-radius: 8px;
}

.wdActions {
  display: flex;
  gap: 0.625rem;
  margin-top: 1.75rem;
}

.wdAddBtn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.6875rem 1.5rem;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--text-accent), #06b6d4);
  color: var(--bg-primary);
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  text-align: center;
  letter-spacing: -0.01em;
}

.wdAddBtn:hover {
  box-shadow: 0 4px 20px rgba(34, 211, 238, 0.25);
  transform: translateY(-1px);
}

.wdAddBtn:active {
  transform: translateY(0);
}

.wdAddBtn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.wdSecondaryBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.6875rem 1.25rem;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background: transparent;
  color: var(--text-secondary);
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  text-decoration: none;
}

.wdSecondaryBtn:hover {
  color: var(--text-primary);
  border-color: var(--text-secondary);
  background: var(--btn-hover-bg);
}

/* Success state */
.wdSuccess {
  text-align: center;
  padding: 1.5rem 0 0.5rem;
}

.wdSuccessIcon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: var(--color-success);
  animation: wdFadeUp 0.3s ease-out;
}

.wdSuccessText {
  font-family: var(--font-heading);
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.375rem;
}

.wdSuccessSub {
  font-family: var(--font-body);
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0 0 1.5rem;
}

.wdSpinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.wdSeoContent {
  position: relative;
  z-index: 1;
  max-width: 540px;
  width: 100%;
  margin-top: -1px;
  padding: 1.25rem 2rem 1.5rem;
  font-size: 0.78rem;
  line-height: 1.7;
  color: var(--text-secondary, #94a3b8);
  opacity: 0.5;
  border-top: 1px solid var(--card-border, rgba(255, 255, 255, 0.06));
}

.wdSeoContent h2 {
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--text-secondary, #94a3b8);
  margin: 0 0 0.35rem;
  letter-spacing: 0.01em;
}

.wdSeoContent p {
  margin: 0 0 0.35rem;
}

.wdSeoContent p:last-child {
  margin-bottom: 0;
}

.wdSeoContent a {
  color: inherit;
  text-decoration: underline;
  text-decoration-color: rgba(148, 163, 184, 0.35);
  text-underline-offset: 2px;
  transition: opacity 0.15s;
}

.wdSeoContent a:hover {
  opacity: 1;
}

.wdLiveSignals {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.75rem;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  opacity: 1;
}

.wdLiveSignals span {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: var(--text-accent, #22d3ee);
}

.wdRelated {
  position: relative;
  z-index: 1;
  max-width: 540px;
  width: 100%;
  margin-top: 1.5rem;
}

.wdRelatedTitle {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-secondary, #94a3b8);
  opacity: 0.6;
  margin: 0 0 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.wdRelatedGrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
}

.wdRelatedCard {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--card-border, rgba(255, 255, 255, 0.06));
  border-radius: 8px;
  text-decoration: none;
  color: var(--text-secondary, #94a3b8);
  font-size: 0.78rem;
  transition: border-color 0.15s, color 0.15s;
  min-width: 0;
  overflow: hidden;
}

.wdRelatedCard:hover {
  border-color: var(--text-accent, #22d3ee);
  color: var(--text-primary, #e2e8f0);
}

.wdRelatedFavicon {
  width: 16px;
  height: 16px;
  border-radius: 2px;
  flex-shrink: 0;
}

.wdRelatedName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wdFooter {
  position: relative;
  z-index: 1;
  display: flex;
  gap: 1.5rem;
  margin-top: 2rem;
  font-size: 0.8rem;
}

.wdFooter a {
  color: var(--text-secondary);
  text-decoration: none;
  transition: color 0.15s;
}

.wdFooter a:hover {
  color: var(--text-accent);
}

/* Light theme */
@media (max-width: 480px) {
  .wdContainer {
    padding: 1.25rem 1rem 2rem;
  }

  .wdCard {
    padding: 1.25rem;
  }

  .wdActions {
    flex-direction: column;
  }

  .wdConfigGrid {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }
}

.spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ─── Category page layout ─── */
.catMain {
  min-height: 100vh;
  min-height: 100dvh;
  position: relative;
  background: var(--bg-primary);
  color: var(--text-primary);
}

.catSection {
  position: relative;
  z-index: 1;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem 2rem;
}

.catSectionTitle {
  font-family: var(--font-heading);
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem;
}

/* ─── Category page hero ─── */
.categoryHero {
  position: relative;
  z-index: 1;
  max-width: 900px;
  margin: 0 auto;
  padding: 1.5rem 2rem 1.25rem;
  border-bottom: 1px solid var(--card-border);
  margin-bottom: 1.25rem;
}

.categoryHeroIcon {
  font-size: 2rem;
  margin-bottom: 0.25rem;
}

.categoryHeroTitle {
  font-family: var(--font-heading);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.25rem;
}

.categoryHeroDesc {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0;
}

/* ─── Controls (sort, filter) ─── */
.controls {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 1rem;
}

.sortToggle {
  display: flex;
  gap: 0;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid var(--card-border);
}

.sortBtn {
  padding: 0.4rem 0.85rem;
  font-size: 0.75rem;
  font-weight: 500;
  background: transparent;
  color: var(--text-secondary);
  border: none;
  cursor: pointer;
  transition: all 0.15s;
}

.sortBtn:hover {
  color: var(--text-primary);
}

.sortBtnActive {
  background: rgba(34, 211, 238, 0.1);
  color: var(--text-accent);
}

/* ─── Detail page back nav ─── */
.header {
  position: relative;
  z-index: 1;
  max-width: 720px;
  margin: 0 auto;
  padding: 1.5rem 2rem;
}

.backLink {
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.85rem;
  transition: color 0.15s;
}

.backLink:hover {
  color: var(--text-accent);
}

.wdFaqSection {
  margin-top: 0.5rem;
}

.wdFaqTitle {
  font-family: var(--font-heading);
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.6rem;
  letter-spacing: -0.01em;
}

/* ─── FAQ items on watch detail ─── */
.wdFaqItem {
  border: 1px solid color-mix(in srgb, var(--text-secondary) 20%, transparent);
  border-radius: 10px;
  padding: 0;
  margin-bottom: 0.5rem;
  background: color-mix(in srgb, var(--text-secondary) 4%, transparent);
}

.wdFaqItem summary {
  padding: 0.7rem 0.9rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--text-primary);
  list-style: none;
}

.wdFaqItem summary::marker,
.wdFaqItem summary::-webkit-details-marker {
  display: none;
}

.wdFaqItem[open] summary {
  border-bottom: 1px solid color-mix(in srgb, var(--text-secondary) 15%, transparent);
}

.wdFaqItem p {
  padding: 0.6rem 0.9rem;
  margin: 0;
  font-size: 0.82rem;
  color: var(--text-secondary);
  line-height: 1.55;
}

/* ─── Cross-category links ─── */
.crossCategories {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-light);
}

.crossCategoriesTitle {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.75rem;
}

.crossCategoriesGrid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

@media (max-width: 768px) {
  .crossCategoriesGrid {
    gap: 0.35rem;
  }

  .crossCategoryLink {
    font-size: 0.78rem;
    padding: 0.35rem 0.6rem;
  }
}

.crossCategoryLink {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.82rem;
  transition: background 0.15s, color 0.15s;
}

.crossCategoryLink:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

/* ─── Footer ─── */
.footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  max-width: 900px;
  margin: 2rem auto 0;
  padding: 2rem 1rem 1.5rem;
  border-top: 1px solid var(--border-light);
}

.footer a {
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.82rem;
}

.footer a:hover {
  color: var(--text-primary);
}

/* ─── Responsive ─── */
@media (max-width: 768px) {
  .page {
    height: auto;
    overflow: auto;
  }

  .layout,
  .layoutWithDetail {
    grid-template-columns: 1fr;
    padding: 0.75rem;
    gap: 0.75rem;
  }

  .feed,
  .detailPanel {
    overflow: visible;
    padding: 0;
  }

  .sidebar {
    border-right: none;
    border-bottom: 1px solid var(--card-border);
    padding: 0 0 0.5rem;
    position: static;
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    min-width: 0;
  }

  .sidebar::-webkit-scrollbar {
    display: none;
  }

  .sidebarNav {
    display: block;
    white-space: nowrap;
    padding: 0.5rem 0.25rem;
  }

  .sidebarItem {
    white-space: nowrap;
    padding: 0.4rem 0.75rem;
    min-height: 36px;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    width: auto;
    border: 1px solid var(--card-border);
    border-radius: 999px;
    font-size: 0.82rem;
    vertical-align: middle;
    margin-right: 4px;
  }

  .sidebarItemActive {
    border-color: rgba(34, 211, 238, 0.3);
    border-left-color: rgba(34, 211, 238, 0.3);
  }

  .sidebarItemLabel {
    flex: none;
    overflow: visible;
  }

  .sidebarItemCount {
    font-size: 0.6rem;
    margin-left: 0;
    opacity: 0.4;
  }

  .sidebarTitle {
    display: none;
  }

  .detailPanel {
    position: fixed;
    inset: 0;
    top: 52px;
    z-index: 20;
    background: var(--bg-primary);
    border-left: none;
    padding: 1.25rem 1rem;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .topBarInner {
    padding: 0 0.75rem;
    gap: 0.5rem;
  }

  .topBarTitle,
  .topBarDivider {
    display: none;
  }

  .topBarSearch {
    max-width: none;
    flex: 1;
    min-width: 0;
    margin-left: 0;
  }

  .searchInput {
    font-size: 0.85rem;
    padding: 0.5rem 0.75rem 0.5rem 2.1rem;
  }

  .publishCta {
    padding: 0.45rem 0.75rem;
    font-size: 0.75rem;
  }

  .publishCta span {
    display: none;
  }

  .feedHeader {
    gap: 0.5rem;
  }

  .viewToggle {
    display: none;
  }

  .feedCount {
    display: none;
  }

  /* Grid cards (forced on mobile) */
  .watchGrid {
    grid-template-columns: 1fr;
    gap: 0.6rem;
  }

  .gridCard {
    padding: 0.85rem 1rem;
  }

  .gridCardFavicon {
    width: 26px;
    height: 26px;
    border-radius: 6px;
  }

  .gridCardTitle {
    font-size: 0.88rem;
    margin: 0 0 0.35rem;
    -webkit-line-clamp: 1;
  }

  .gridCardDomain {
    font-size: 0.72rem;
  }

  .gridCardMeta {
    padding-top: 0.45rem;
  }

  .gridCardBadge {
    font-size: 0.65rem;
  }

  .gridCardClones {
    font-size: 0.68rem;
  }

  /* Share buttons */
  .detailShareBtn {
    width: 44px;
    height: 44px;
  }

  /* Hide list-view styles (not used on mobile) */
  .card {
    display: none;
  }

  /* Detail page ($slug) */
  .detail {
    padding: 1.25rem 1rem 2rem;
  }

  .header {
    padding: 1rem;
  }

  .cta {
    padding: 0 1rem 2rem;
  }

  .ctaCard {
    padding: 1.5rem 1rem;
  }

  .configGrid {
    grid-template-columns: 1fr;
  }

  /* Footer */
  .footer {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 2rem 1rem 1rem;
    font-size: 0.8rem;
  }
}

/* Small phones */
@media (max-width: 380px) {
  .layout,
  .layoutWithDetail {
    padding: 0.5rem;
  }

  .topBarInner {
    padding: 0 0.5rem;
    gap: 0.35rem;
  }

  .publishCta {
    padding: 0.4rem 0.6rem;
    font-size: 0.72rem;
  }

  .card {
    padding: 0.75rem 0.5rem;
  }

  .gridCard {
    padding: 0.85rem 1rem;
  }

  .detailPanel {
    padding: 1rem 0.75rem;
  }
}
